import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdminAuth } from "./AdminAuthContext";
import { adminApi, formatApiErrorDetail, setToken } from "./api";

export default function Settings() {
  const { user, logout } = useAdminAuth();
  const [newEmail, setNewEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [pwBusy, setPwBusy] = useState(false);
  const [pwErr, setPwErr] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [pwCur, setPwCur] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConf, setPwConf] = useState("");

  const submitEmail = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(""); setErr("");
    try {
      await adminApi.changeEmail({ new_email: newEmail.trim().toLowerCase() });
      setMsg("Email changed. Signing you out — please sign in again with the new address.");
      setTimeout(() => logout(), 1600);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Change failed.");
    } finally { setBusy(false); }
  };

  const submitPw = async (e) => {
    e.preventDefault();
    setPwBusy(true); setPwErr(""); setPwMsg("");
    if (pwNew !== pwConf) { setPwErr("The two new passwords don't match."); setPwBusy(false); return; }
    try {
      const { data } = await adminApi.changeMyPassword(pwCur, pwNew);
      if (data?.access_token) setToken(data.access_token);
      setPwMsg("Password changed. Other sessions on this account are now signed out.");
      setPwCur(""); setPwNew(""); setPwConf("");
    } catch (e2) {
      setPwErr(formatApiErrorDetail(e2.response?.data?.detail) || "Change failed.");
    } finally { setPwBusy(false); }
  };

  return (
    <div className="ga-page" data-testid="settings-page">
      <div className="ga-eyebrow">Settings</div>
      <h1 className="ga-title">Your admin account</h1>

      <h2 className="ga-group__title" style={{ marginTop: 24 }}>Login email</h2>
      <form onSubmit={submitEmail} className="ga-form" style={{ maxWidth: 480 }}>
        <label className="ga-field ga-field--wide">
          <span>Current email</span>
          <input type="email" disabled value={user?.email || ""} data-testid="settings-current-email" />
        </label>
        <label className="ga-field ga-field--wide">
          <span>New email</span>
          <input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="settings-new-email" />
        </label>
        <div className="ga-savebar">
          <div>
            {err && <span className="ga-error" data-testid="settings-err">{err}</span>}
            {msg && <span className="ga-saved" data-testid="settings-msg">{msg}</span>}
          </div>
          <button type="submit" className="ga-btn" disabled={busy || !newEmail || newEmail === user?.email} data-testid="settings-submit">
            {busy ? "Saving…" : "Change email"}
          </button>
        </div>
      </form>

      <h2 className="ga-group__title" style={{ marginTop: 48 }}>Change my password</h2>
      <form onSubmit={submitPw} className="ga-form" style={{ maxWidth: 480 }} data-testid="settings-pw-form">
        <label className="ga-field ga-field--wide">
          <span>Current password</span>
          <input type="password" required value={pwCur} onChange={(e) => setPwCur(e.target.value)} data-testid="settings-pw-current" />
        </label>
        <label className="ga-field ga-field--wide">
          <span>New password</span>
          <input type="password" required minLength={12} value={pwNew} onChange={(e) => setPwNew(e.target.value)} data-testid="settings-pw-new" />
        </label>
        <label className="ga-field ga-field--wide">
          <span>Confirm new password</span>
          <input type="password" required minLength={12} value={pwConf} onChange={(e) => setPwConf(e.target.value)} data-testid="settings-pw-conf" />
        </label>
        <p className="ga-mute" style={{ fontSize: 12 }}>Minimum 12 characters, with at least one letter and one number. Other sessions on this account will be signed out.</p>
        <div className="ga-savebar">
          <div>
            {pwErr && <span className="ga-error" data-testid="settings-pw-err">{pwErr}</span>}
            {pwMsg && <span className="ga-saved" data-testid="settings-pw-msg">{pwMsg}</span>}
          </div>
          <button type="submit" className="ga-btn" disabled={pwBusy || !pwCur || !pwNew} data-testid="settings-pw-submit">
            {pwBusy ? "Saving…" : "Change password"}
          </button>
        </div>
      </form>

      <h2 className="ga-group__title" style={{ marginTop: 48 }}>Administrators</h2>
      <p className="ga-mute" style={{ maxWidth: "60ch" }}>
        Invite additional admins, view sign-in history, revoke access, or regenerate a setup link for someone who lost theirs.
      </p>
      <p><Link to="/admin/settings/users" className="ga-link" data-testid="settings-manage-users">Manage administrators →</Link></p>
    </div>
  );
}
